import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { attendanceService } from "../../firebase/services";
import {
  CalendarCheck,
  CheckCircle2,
  XCircle,
  Clock,
  Award,
  Loader2,
  AlertCircle,
} from "lucide-react";

export const StudentAttendance = () => {
  const { userProfile, currentUser } = useAuth();
  const [attendanceList, setAttendanceList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        if (userProfile?.studentId) {
          const list = await attendanceService.getStudentAttendance(currentUser?.uid);
          setAttendanceList(list);
        }
      } catch (err) {
        console.error("Attendance fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [userProfile]);

  const presentCount = attendanceList.filter((r) => r.status === "Present").length;
  const totalRecorded = attendanceList.length;
  const percentage = totalRecorded > 0 ? Math.round((presentCount / totalRecorded) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">15 Days Masterclass Attendance</h1>
          <p className="text-xs text-muted-foreground font-mono">
            Student: {userProfile?.fullName || "Student"} ({userProfile?.studentId || "KIAN-CYBER-PENDING"})
          </p>
        </div>

        {/* Top Stat Badge */}
        <div className="flex items-center space-x-3 bg-card border border-border px-4 py-2 rounded-xl shadow-sm">
          <CalendarCheck className="w-5 h-5 text-emerald-400" />
          <div className="text-right font-mono">
            <div className="text-xs text-muted-foreground">Attendance Score</div>
            <div className="text-sm font-bold text-foreground">
              {presentCount} / {totalRecorded} Days ({percentage}%)
            </div>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="text-xs font-mono text-muted-foreground mb-1">Present Sessions</div>
          <div className="text-2xl font-bold text-emerald-400 font-mono flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" />
            <span>{presentCount} Days</span>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="text-xs font-mono text-muted-foreground mb-1">Absent Sessions</div>
          <div className="text-2xl font-bold text-rose-400 font-mono flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            <span>{totalRecorded - presentCount} Days</span>
          </div>
        </div>

        <div className="p-4 rounded-xl bg-card border border-border">
          <div className="text-xs font-mono text-muted-foreground mb-1">Certificate Criterion</div>
          <div className="text-xs font-semibold text-foreground flex items-center gap-2 mt-1">
            <Award className="w-5 h-5 text-amber-400" />
            <span>Minimum 85% attendance required</span>
          </div>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-card border border-border rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <h2 className="font-semibold text-sm">Attendance Log & Session Topics</h2>
          <span className="text-xs font-mono text-muted-foreground">
            {totalRecorded} records found
          </span>
        </div>

        {loading ? (
          <div className="p-12 text-center text-muted-foreground flex flex-col items-center justify-center gap-2">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            <span className="text-xs font-mono">Loading real attendance from Firestore...</span>
          </div>
        ) : attendanceList.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground space-y-2">
            <AlertCircle className="w-8 h-8 mx-auto text-muted-foreground/50" />
            <div className="text-sm font-semibold text-foreground">No attendance records available.</div>
            <p className="text-xs max-w-sm mx-auto font-mono">
              Attendance will appear here after the instructor conducts the daily class roll-call in the admin portal.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-mono">
              <thead className="bg-muted/50 border-b border-border text-muted-foreground uppercase text-[10px]">
                <tr>
                  <th className="px-4 py-3">Day</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Session Topic</th>
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {attendanceList.map((rec) => (
                  <tr key={rec.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3 font-bold text-emerald-400">
                      Day {rec.dayNumber}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{rec.date}</td>
                    <td className="px-4 py-3 text-foreground font-sans font-medium">
                      {rec.topic}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {rec.status === "Present" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-semibold">
                          <CheckCircle2 className="w-3 h-3" />
                          Present
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/20 font-semibold">
                          <XCircle className="w-3 h-3" />
                          Absent
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
